import { useCallback, useEffect, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { AxiosError } from "axios";
import {
  adminApi,
  refreshAdminToken,
  setAdminToken,
  type ReferralAdminAmbassadorContractDetail,
  type ReferralAdminAmbassadorRow,
  type ReferralAdminCodeDetail,
  type ReferralAdminCodeSaveParam,
  type ReferralAdminSettlementResult,
} from "api/adminApi";
import { type AmbassadorDashboardResult } from "api/ambassadorApi";
import {
  formatPhoneNumberInput,
  normalizePhoneNumber,
} from "utils/phoneNumberUtils";
import { CONTRACT_FILE_MAX_BYTES, readFileAsDataUrl } from "utils/fileUtils";

type AuthPhase = "checking" | "login" | "denied" | "ready";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

const currentMonth = () => new Date().toISOString().slice(0, 7);

// 좌측 nav 메뉴 — 디스턴싱 백오피스의 도구 단위. ready=false 는 자리만 잡아둔 준비중 메뉴.
type AdminMenu = "creator" | "member" | "coach";

const ADMIN_MENUS: { key: AdminMenu; label: string; ready: boolean }[] = [
  { key: "creator", label: "크리에이터 협업", ready: true },
  { key: "member", label: "회원관리", ready: false },
  { key: "coach", label: "코치 관리", ready: false },
];

// 크리에이터 협업 하위 메뉴 — 계약은 현재 앰버서더 계약만 다룬다.
type CreatorTab = "code" | "contract" | "performance" | "settlement";

const CREATOR_TABS: { key: CreatorTab; label: string }[] = [
  { key: "contract", label: "계약" },
  { key: "code", label: "링크 관리" },
  { key: "performance", label: "성과" },
  { key: "settlement", label: "정산" },
];

// 디스턴싱 백오피스 — @orwellhealth.org 구글 계정 중 서버 허용 목록만 접근 가능.
// 좌측 nav 로 도구를 구분한다: 크리에이터 협업(하위 메뉴: 계약·링크 관리·성과·정산) + 회원관리(준비중).
// 크리에이터 협업은 referral 전체 타입을 관리한다: affiliate(외부 명칭 "앰버서더", 정산 대상) + discount(단순할인).
// 인증은 어드민 전용 인스턴스(adminApi)가 관리 — 기존 유저/프로모션 세션과 완전 분리.
export default function AdminPage() {
  const [phase, setPhase] = useState<AuthPhase>("checking");
  const [menu, setMenu] = useState<AdminMenu>("creator");
  const [creatorTab, setCreatorTab] = useState<CreatorTab>("contract");
  const [error, setError] = useState<string | null>(null);
  const [codes, setCodes] = useState<ReferralAdminCodeDetail[]>([]);

  const enter = useCallback(async () => {
    try {
      const res = await adminApi.codeList();
      if (res.code === 0) {
        setCodes(res.result ?? []);
        setPhase("ready");
      } else {
        setError(res.message || FALLBACK_ERROR_MESSAGE);
        setPhase("login");
      }
    } catch (e) {
      const status = (e as AxiosError).response?.status;
      setPhase(status === 403 ? "denied" : "login");
    }
  }, []);

  useEffect(() => {
    (async () => {
      const token = await refreshAdminToken();
      if (!token) {
        setPhase("login");
        return;
      }
      await enter();
    })();
  }, [enter]);

  const reloadCodes = async () => {
    const res = await adminApi.codeList();
    if (res.code === 0) setCodes(res.result ?? []);
  };

  const handleGoogleLogin = async (credential?: string) => {
    if (!credential) return;
    setError(null);
    try {
      const res = await adminApi.login(credential);
      if (res.code === 0 && res.result?.accessToken) {
        setAdminToken(res.result.accessToken);
        await enter();
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
    }
  };

  // 로그인 전(확인/로그인/거부)에는 nav 없이 단순 화면, 로그인 후에는 좌측 nav + 콘텐츠 레이아웃.
  if (phase !== "ready") {
    return (
      <div className="w-full h-full overflow-y-auto bg-gray-50">
        <div className="mx-auto w-full max-w-[960px] px-5 py-10">
          <h1 className="text-xl font-bold text-gray-900">디스턴싱 어드민</h1>

          {phase === "checking" && (
            <p className="mt-10 text-sm text-gray-500">확인 중...</p>
          )}

          {phase === "login" && (
            <div className="mt-10 flex flex-col items-start gap-4">
              <p className="text-sm text-gray-500">
                @orwellhealth.org 구글 계정으로 로그인해 주세요.
              </p>
              <GoogleLogin
                onSuccess={(response) => handleGoogleLogin(response.credential)}
                onError={() => setError(FALLBACK_ERROR_MESSAGE)}
              />
              {error && <p className="text-sm text-red-500">{error}</p>}
            </div>
          )}

          {phase === "denied" && (
            <p className="mt-10 text-sm text-red-500">
              권한이 없는 계정이에요. 관리자에게 어드민 등록을 요청해 주세요.
            </p>
          )}
        </div>
      </div>
    );
  }

  const activeMenu = ADMIN_MENUS.find(({ key }) => key === menu);

  return (
    <div className="flex w-full h-full bg-gray-50">
      {/* 좌측 nav — 콘텐츠만 스크롤되고 nav 는 고정된다 */}
      <aside className="w-56 shrink-0 border-r border-gray-200 bg-white px-4 py-8">
        <h1 className="px-3 text-lg font-bold text-gray-900">
          디스턴싱 어드민
        </h1>
        <nav className="mt-6 flex flex-col gap-1">
          {ADMIN_MENUS.map(({ key, label, ready }) => (
            <div key={key} className="flex flex-col gap-1">
              <button
                onClick={() => setMenu(key)}
                className={`flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-medium ${
                  menu === key
                    ? key === "creator"
                      ? "bg-gray-100 text-gray-900"
                      : "bg-gray-900 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {label}
                {!ready && (
                  <span
                    className={`text-[11px] ${
                      menu === key ? "text-gray-300" : "text-gray-400"
                    }`}
                  >
                    준비중
                  </span>
                )}
              </button>
              {/* 하위 메뉴 — 상위 메뉴가 선택된 동안만 펼친다 */}
              {key === "creator" && menu === "creator" && (
                <div className="flex flex-col gap-1 pl-3">
                  {CREATOR_TABS.map(({ key: tabKey, label: tabLabel }) => (
                    <button
                      key={tabKey}
                      onClick={() => setCreatorTab(tabKey)}
                      className={`rounded-lg px-3 py-1.5 text-left text-sm ${
                        creatorTab === tabKey
                          ? "bg-gray-900 font-medium text-white"
                          : "text-gray-500 hover:bg-gray-100"
                      }`}
                    >
                      {tabLabel}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[960px] px-5 py-10">
          <h1 className="text-xl font-bold text-gray-900">
            {activeMenu?.label}
          </h1>

          {menu === "creator" && (
            <div className="mt-8">
              {creatorTab === "code" && (
                <CodeSection codes={codes} onSaved={reloadCodes} />
              )}
              {creatorTab === "contract" && <AmbassadorSection />}
              {creatorTab === "performance" && (
                <PerformanceSection codes={codes} />
              )}
              {creatorTab === "settlement" && <SettlementSection />}
            </div>
          )}

          {/* 준비중 메뉴 공통 플레이스홀더 (회원관리·코치 관리) */}
          {!activeMenu?.ready && (
            <p className="mt-10 text-sm text-gray-500">
              아직 준비 중인 기능이에요.
            </p>
          )}
        </div>
      </main>
    </div>
  );
}

// ---------- 코드 관리 ----------

type CodeFormState = {
  code: string;
  name: string;
  type: string;
  isActive: boolean;
  startDate: string;
  durationDays: string;
  ambassadorPhoneNumber: string;
  perPayerAmount: string;
};

const emptyForm: CodeFormState = {
  code: "",
  name: "",
  type: "affiliate",
  isActive: true,
  startDate: "",
  durationDays: "",
  ambassadorPhoneNumber: "",
  perPayerAmount: "",
};

// 만료일은 직접 입력하지 않고 시작일 + 유효 일수로 자동 계산한다 (일수 미입력 시 무기한).
const todayDate = () => new Date().toISOString().slice(0, 10);

const addDays = (date: string, days: number) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const diffDays = (start: string, end: string) =>
  Math.round(
    (new Date(end).getTime() - new Date(start).getTime()) / 86_400_000
  );

// 앰버서더는 affiliate 의 외부 명칭 — 화면 표기는 앰버서더/할인으로 통일
const typeLabel = (type: string) => (type === "discount" ? "할인" : "앰버서더");

function CodeSection({
  codes,
  onSaved,
}: {
  codes: ReferralAdminCodeDetail[];
  onSaved: () => Promise<void>;
}) {
  // editingCode: null=폼 닫힘, ""=신규, 그 외=해당 코드 수정
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [form, setForm] = useState<CodeFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const isNew = editingCode === "";
  // 단순할인 코드는 앰버서더 계정·정산 개념이 없다 — 관련 입력을 숨기고 null 로 저장한다
  const isDiscount = form.type === "discount";

  const openNew = () => {
    setEditingCode("");
    setForm({ ...emptyForm, startDate: todayDate() });
    setMessage(null);
  };

  const openEdit = (detail: ReferralAdminCodeDetail) => {
    // 만료일이 있는데 시작일이 없는 기존 코드는 시작일을 오늘로 채워 일수를 역산한다
    // (시작일 없이는 일수 계산이 불가능하고, 비워 두면 저장 시 만료일이 사라지기 때문).
    const startDate = detail.startDate ?? (detail.endDate ? todayDate() : "");
    setEditingCode(detail.code);
    setForm({
      code: detail.code,
      name: detail.name ?? "",
      type: detail.type,
      isActive: detail.isActive,
      startDate,
      durationDays: detail.endDate
        ? String(diffDays(startDate, detail.endDate))
        : "",
      ambassadorPhoneNumber: detail.ambassadorPhoneNumber
        ? formatPhoneNumberInput(detail.ambassadorPhoneNumber)
        : "",
      perPayerAmount:
        detail.perPayerAmount != null ? String(detail.perPayerAmount) : "",
    });
    setMessage(null);
  };

  // 만료일 = 시작일 + 유효 일수 (자동 계산 — 일수를 비우면 무기한)
  const computedEndDate =
    form.startDate && form.durationDays
      ? addDays(form.startDate, Number(form.durationDays))
      : "";

  const save = async () => {
    if (saving || form.code.trim().length === 0) return;
    setSaving(true);
    setMessage(null);
    const param: ReferralAdminCodeSaveParam = {
      code: form.code.trim(),
      name: form.name.trim() || null,
      type: form.type,
      isActive: form.isActive,
      startDate: form.startDate || null,
      endDate: computedEndDate || null,
      ambassadorPhoneNumber: isDiscount
        ? null
        : normalizePhoneNumber(form.ambassadorPhoneNumber) || null,
      perPayerAmount:
        isDiscount || !form.perPayerAmount ? null : Number(form.perPayerAmount),
    };
    try {
      const res = isNew
        ? await adminApi.codeCreate(param)
        : await adminApi.codeUpdate(param);
      if (res.code === 0) {
        setEditingCode(null);
        await onSaved();
      } else {
        setMessage(res.message || FALLBACK_ERROR_MESSAGE);
      }
    } catch (e) {
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  const inputClassName =
    "rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-900";

  return (
    <section>
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold text-gray-900">링크 관리</h2>
        <button
          className="rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white"
          onClick={openNew}
        >
          새 코드 발급
        </button>
      </div>

      {editingCode !== null && (
        <div className="mt-3 rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm font-semibold text-gray-900">
            {isNew ? "새 코드 발급" : `코드 수정 — ${editingCode}`}
          </p>
          <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3">
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              코드 (/with/코드)
              <input
                className={inputClassName}
                value={form.code}
                disabled={!isNew}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              표시 이름
              <input
                className={inputClassName}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              유형
              <select
                className={inputClassName}
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <option value="affiliate">앰버서더 (정산 대상)</option>
                <option value="discount">단순할인 (정산 없음)</option>
              </select>
            </label>
            {!isDiscount && (
              <label className="flex flex-col gap-1 text-xs text-gray-500">
                앰버서더 전화번호 (대시보드 계정)
                <input
                  className={inputClassName}
                  type="tel"
                  value={form.ambassadorPhoneNumber}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      ambassadorPhoneNumber: formatPhoneNumberInput(
                        e.target.value
                      ),
                    })
                  }
                />
              </label>
            )}
            {!isDiscount && (
              <label className="flex flex-col gap-1 text-xs text-gray-500">
                결제자 1인당 정산 단가 (원)
                <input
                  className={inputClassName}
                  type="number"
                  min={0}
                  value={form.perPayerAmount}
                  onChange={(e) =>
                    setForm({ ...form, perPayerAmount: e.target.value })
                  }
                />
                <span className="text-[11px] text-gray-400">
                  변경하면 이번 달부터 적용돼요 — 지난 달 정산액은 바뀌지 않아요
                </span>
              </label>
            )}
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              시작일
              <input
                className={inputClassName}
                type="date"
                value={form.startDate}
                onChange={(e) =>
                  setForm({ ...form, startDate: e.target.value })
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              유효 일수 (비우면 무기한)
              <input
                className={inputClassName}
                type="number"
                min={1}
                value={form.durationDays}
                onChange={(e) =>
                  setForm({ ...form, durationDays: e.target.value })
                }
              />
              <span className="text-[11px] text-gray-400">
                {computedEndDate
                  ? `만료일 ${computedEndDate}`
                  : form.durationDays
                    ? "시작일을 설정하면 만료일이 계산돼요"
                    : "만료일 없이 무기한 활성"}
              </span>
            </label>
          </div>
          {!isNew && (
            <label className="mt-3 flex items-center gap-1.5 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) =>
                  setForm({ ...form, isActive: e.target.checked })
                }
              />
              활성 (끄면 /with/{form.code} 게이트가 무효 처리돼요)
            </label>
          )}
          {message && <p className="mt-3 text-sm text-red-500">{message}</p>}
          <div className="mt-4 flex gap-2">
            <button
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
              disabled={saving || form.code.trim().length === 0}
              onClick={save}
            >
              {saving ? "저장 중..." : "저장"}
            </button>
            <button
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600"
              onClick={() => setEditingCode(null)}
            >
              취소
            </button>
          </div>
        </div>
      )}

      <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-100 text-left text-gray-500">
              <th className="px-3 py-3 font-medium">이름</th>
              <th className="px-3 py-3 font-medium">유형</th>
              <th className="px-3 py-3 font-medium">코드</th>
              <th className="px-3 py-3 font-medium">전화번호</th>
              <th className="px-3 py-3 text-right font-medium">현재 단가</th>
              <th className="px-3 py-3 font-medium">기간</th>
              <th className="px-3 py-3 font-medium">상태</th>
              <th className="px-3 py-3 font-medium">계정</th>
              <th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 && (
              <tr>
                <td className="px-3 py-6 text-center text-gray-400" colSpan={9}>
                  등록된 코드가 없어요
                </td>
              </tr>
            )}
            {codes.map((detail) => (
              <tr
                key={detail.code}
                className="border-b border-gray-100 last:border-b-0"
              >
                <td className="px-3 py-3 text-gray-900">
                  {detail.name ?? "-"}
                </td>
                <td className="px-3 py-3 text-gray-500">
                  {typeLabel(detail.type)}
                </td>
                <td className="px-3 py-3 font-medium text-gray-900">
                  {detail.code}
                </td>
                <td className="px-3 py-3 text-gray-900">
                  {detail.ambassadorPhoneNumber
                    ? formatPhoneNumberInput(detail.ambassadorPhoneNumber)
                    : "-"}
                </td>
                <td className="px-3 py-3 text-right text-gray-900">
                  {detail.perPayerAmount != null
                    ? `${detail.perPayerAmount.toLocaleString()}원`
                    : "-"}
                </td>
                <td className="px-3 py-3 text-gray-500">
                  {detail.startDate ?? "…"} ~ {detail.endDate ?? "무기한"}
                </td>
                <td className="px-3 py-3">
                  <span
                    className={
                      detail.isActive ? "text-green-600" : "text-gray-400"
                    }
                  >
                    {detail.isActive ? "활성" : "비활성"}
                  </span>
                </td>
                <td className="px-3 py-3 text-gray-500">
                  {detail.accountStatus}
                </td>
                <td className="px-3 py-3 text-right">
                  <button
                    className="text-gray-500 underline"
                    onClick={() => openEdit(detail)}
                  >
                    수정
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------- 앰버서더 계정·계약 정보 ----------

const contractTypeLabel = (type?: string | null) => {
  if (type === "freelancer") return "프리랜서";
  if (type === "business_general") return "사업자(일반)";
  if (type === "business_simplified") return "사업자(간이)";
  return "-";
};

// data URL 의 MIME 으로 다운로드 파일 확장자를 정한다 (예: data:application/pdf;... → pdf)
const fileExtOf = (dataUrl: string) => {
  const mime = dataUrl.slice(5, dataUrl.indexOf(";"));
  if (mime === "application/pdf") return "pdf";
  return mime.split("/")[1] || "bin";
};

// 앰버서더 계정 목록 + 계약 정보 상세(파일 다운로드) — 계약서 발송 실무용.
// 목록은 파일 제외 요약이고, 파일(base64)은 행을 눌렀을 때만 상세 API 로 로드한다.
function AmbassadorSection() {
  const [rows, setRows] = useState<ReferralAdminAmbassadorRow[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [detail, setDetail] =
    useState<ReferralAdminAmbassadorContractDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminApi.ambassadorList();
        if (res.code === 0) setRows(res.result ?? []);
        else setMessage(res.message || FALLBACK_ERROR_MESSAGE);
      } catch (e) {
        setMessage(FALLBACK_ERROR_MESSAGE);
      }
    })();
  }, []);

  const openDetail = async (ambassadorId: number) => {
    if (detailLoading) return;
    setDetailLoading(true);
    setMessage(null);
    try {
      const res = await adminApi.ambassadorContract(ambassadorId);
      if (res.code === 0 && res.result) setDetail(res.result);
      else setMessage(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setDetailLoading(false);
    }
  };

  // 계약 완료 처리 팝업 상태 — 완료로 바꿀 때 체결된 계약서 사본을 함께 업로드할 수 있다.
  const [completing, setCompleting] =
    useState<ReferralAdminAmbassadorRow | null>(null);
  const [completeFile, setCompleteFile] = useState<string | null>(null);
  const [completeFileName, setCompleteFileName] = useState<string | null>(null);
  const [completeError, setCompleteError] = useState<string | null>(null);
  const [completeSubmitting, setCompleteSubmitting] = useState(false);
  const [completeDragging, setCompleteDragging] = useState(false);

  // 완료/해제 API 호출 — 성공 시 null, 실패 시 오류 메시지를 반환한다 (호출처가 표시 위치를 정한다).
  const applyContractComplete = async (
    row: ReferralAdminAmbassadorRow,
    completed: boolean,
    contractFile: string | null
  ): Promise<string | null> => {
    try {
      const res = await adminApi.ambassadorContractComplete(
        row.ambassadorId,
        completed,
        contractFile
      );
      if (res.code === 0) {
        setRows((prev) =>
          prev.map((r) =>
            r.ambassadorId === row.ambassadorId
              ? { ...r, contractCompleted: completed }
              : r
          )
        );
        return null;
      }
      return res.message || FALLBACK_ERROR_MESSAGE;
    } catch (e) {
      return FALLBACK_ERROR_MESSAGE;
    }
  };

  // 계약 완료 토글 — 완료는 계약서 사본 업로드 팝업을 거치고, 해제는 확인 후 바로 처리한다 (보관된 사본 유지).
  const toggleContractComplete = async (row: ReferralAdminAmbassadorRow) => {
    if (!row.contractCompleted) {
      setCompleting(row);
      setCompleteFile(null);
      setCompleteFileName(null);
      setCompleteError(null);
      return;
    }
    if (
      !window.confirm("계약 완료를 해제할까요? 보관된 계약서 사본은 유지돼요.")
    ) {
      return;
    }
    setMessage(null);
    const error = await applyContractComplete(row, false, null);
    if (error) setMessage(error);
  };

  // 팝업의 파일 선택 — 서버와 동일 상한(5MB), base64 data URL 로 읽어 보관한다.
  // 드롭된 파일은 input accept 필터를 안 거치므로 클릭·드롭 공통으로 형식을 검사한다.
  const handleCompleteFile = async (file?: File) => {
    setCompleteError(null);
    if (!file) return;
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      setCompleteError("이미지 또는 PDF 파일만 올릴 수 있어요.");
      return;
    }
    if (file.size > CONTRACT_FILE_MAX_BYTES) {
      setCompleteError("파일 크기는 5MB 이하로 올려 주세요.");
      return;
    }
    setCompleteFile(await readFileAsDataUrl(file));
    setCompleteFileName(file.name);
  };

  const submitComplete = async () => {
    if (!completing || completeSubmitting) return;
    setCompleteSubmitting(true);
    const error = await applyContractComplete(completing, true, completeFile);
    setCompleteSubmitting(false);
    if (error) {
      setCompleteError(error);
      return;
    }
    setCompleting(null);
  };

  return (
    <section>
      <h2 className="text-base font-bold text-gray-900">앰버서더 계약</h2>
      {message && <p className="mt-3 text-sm text-red-500">{message}</p>}

      <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-100 text-left text-gray-500">
              <th className="px-3 py-3 font-medium">활동명</th>
              <th className="px-3 py-3 font-medium">계약형태</th>
              <th className="px-3 py-3 font-medium">링크</th>
              <th className="px-3 py-3 font-medium">전화번호</th>
              <th className="px-3 py-3 font-medium">계약 정보</th>
              <th className="px-3 py-3 font-medium">계약 상태</th>
              <th className="px-3 py-3 font-medium">가입일</th>
              <th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td className="px-3 py-6 text-center text-gray-400" colSpan={8}>
                  가입한 앰버서더가 없어요
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr
                key={row.ambassadorId}
                className="border-b border-gray-100 last:border-b-0"
              >
                <td className="px-3 py-3 text-gray-900">{row.name ?? "-"}</td>
                <td className="px-3 py-3 text-gray-500">
                  {contractTypeLabel(row.contractType)}
                </td>
                <td className="px-3 py-3 text-gray-500">
                  {row.codes.length > 0 ? row.codes.join(", ") : "-"}
                </td>
                <td className="px-3 py-3 text-gray-900">
                  {row.phoneNumber
                    ? formatPhoneNumberInput(row.phoneNumber)
                    : "-"}
                  {!row.verified && (
                    <span className="ml-1 text-xs text-gray-400">(미인증)</span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <span
                    className={
                      row.contractSubmitted ? "text-green-600" : "text-gray-400"
                    }
                  >
                    {row.contractSubmitted ? "제출" : "미제출"}
                  </span>
                </td>
                <td className="px-3 py-3">
                  {/* 계약 정보 제출 전에는 완료 처리 불가 */}
                  {row.contractSubmitted ? (
                    <button
                      className="underline"
                      onClick={() => toggleContractComplete(row)}
                    >
                      <span
                        className={
                          row.contractCompleted
                            ? "text-green-600"
                            : "text-orange-500"
                        }
                      >
                        {row.contractCompleted ? "계약 완료" : "계약 전"}
                      </span>
                    </button>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-3 py-3 text-gray-500">
                  {row.signedUpDate ?? "-"}
                </td>
                <td className="px-3 py-3 text-right">
                  <button
                    className="text-gray-500 underline disabled:opacity-40"
                    disabled={detailLoading}
                    onClick={() => openDetail(row.ambassadorId)}
                  >
                    계약 상세
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <ContractDetailPanel detail={detail} onClose={() => setDetail(null)} />
      )}

      {/* 계약 완료 처리 팝업 — 체결된 계약서 사본을 함께 보관한다 (선택) */}
      {completing && (
        <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/30 px-5">
          <div className="w-full max-w-[480px] rounded-xl bg-white p-5">
            <p className="text-base font-bold text-gray-900">
              계약 완료 처리 —{" "}
              {completing.name ??
                (completing.phoneNumber
                  ? formatPhoneNumberInput(completing.phoneNumber)
                  : completing.ambassadorId)}
            </p>
            <p className="mt-2 text-sm text-gray-500">
              완료로 바꾸면 앰버서더가 링크를 만들 수 있게 되고, 앰버서더도 계약
              페이지에서 사본을 확인할 수 있어요. 체결된 계약서 사본을 함께
              보관하는 걸 권장해요 (이미지·PDF, 5MB 이하).
            </p>
            {/* 드롭존 — 클릭 선택과 드래그앤드롭 모두 지원 */}
            <div
              className={`mt-4 flex items-center gap-3 rounded-lg border border-dashed p-3 text-sm ${
                completeDragging
                  ? "border-gray-900 bg-gray-50"
                  : "border-gray-300"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setCompleteDragging(true);
              }}
              onDragLeave={(e) => {
                // 자식 요소로 이동할 때 발생하는 leave 는 무시한다 (깜빡임 방지)
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setCompleteDragging(false);
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                setCompleteDragging(false);
                handleCompleteFile(e.dataTransfer.files?.[0]);
              }}
            >
              <label className="shrink-0 cursor-pointer rounded-lg border border-gray-300 px-3 py-2 text-gray-600 hover:bg-gray-50">
                파일 선택
                <input
                  type="file"
                  className="hidden"
                  accept="image/png,image/jpeg,image/gif,image/webp,image/heic,image/heif,application/pdf"
                  onChange={(e) => {
                    handleCompleteFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
              </label>
              <span
                className={
                  completeFileName ? "break-all text-gray-900" : "text-gray-400"
                }
              >
                {completeFileName ??
                  "파일을 끌어다 놓거나 선택하세요 (사본 없이 완료 가능)"}
              </span>
            </div>
            {completeError && (
              <p className="mt-2 text-sm text-red-500">{completeError}</p>
            )}
            <div className="mt-5 flex gap-2">
              <button
                className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
                disabled={completeSubmitting}
                onClick={submitComplete}
              >
                {completeSubmitting ? "처리 중..." : "완료 처리"}
              </button>
              <button
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600"
                onClick={() => setCompleting(null)}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function ContractDetailPanel({
  detail,
  onClose,
}: {
  detail: ReferralAdminAmbassadorContractDetail;
  onClose: () => void;
}) {
  const fileBaseName = detail.phoneNumber ?? String(detail.ambassadorId);

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/30 px-5">
      <div className="max-h-[85vh] w-full max-w-[480px] overflow-y-auto rounded-xl bg-white p-5">
        <p className="text-base font-bold text-gray-900">계약 정보 상세</p>

        {detail.contractType ? (
          <div className="mt-3 flex flex-col gap-2 text-sm">
            <InfoRow label="계약형태">
              {contractTypeLabel(detail.contractType)}
            </InfoRow>
            {detail.realName && (
              <InfoRow label="실명">{detail.realName}</InfoRow>
            )}
            {detail.businessName && (
              <InfoRow label="사업자명">{detail.businessName}</InfoRow>
            )}
            <InfoRow label="계약서 수신 이메일">
              {detail.contractEmail ?? "-"}
            </InfoRow>
            <InfoRow label="핸드폰 번호">
              {detail.contractPhoneNumber
                ? formatPhoneNumberInput(detail.contractPhoneNumber)
                : "-"}
            </InfoRow>
            <InfoRow label="활동명">{detail.name ?? "-"}</InfoRow>
            <ContractFileView
              label="통장사본"
              dataUrl={detail.bankbookFile}
              fileBaseName={`통장사본-${fileBaseName}`}
            />
            {/* 사업자등록증은 사업자 유형의 필수 항목 — 프리랜서 상세에는 표시하지 않는다 */}
            {detail.contractType !== "freelancer" && (
              <ContractFileView
                label="사업자등록증"
                dataUrl={detail.businessRegistrationFile}
                fileBaseName={`사업자등록증-${fileBaseName}`}
              />
            )}
            <ContractFileView
              label="계약서 사본"
              dataUrl={detail.contractFile}
              fileBaseName={`계약서-${fileBaseName}`}
            />
          </div>
        ) : (
          <p className="mt-3 text-sm text-gray-500">
            아직 계약 정보를 제출하지 않았어요.
          </p>
        )}

        <button
          className="mt-4 w-full rounded-lg border border-gray-300 py-2.5 text-sm text-gray-600"
          onClick={onClose}
        >
          닫기
        </button>
      </div>
    </div>
  );
}

function InfoRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-2">
      <span className="w-32 shrink-0 text-gray-500">{label}</span>
      <span className="break-all text-gray-900">{children}</span>
    </div>
  );
}

function ContractFileView({
  label,
  dataUrl,
  fileBaseName,
}: {
  label: string;
  dataUrl?: string | null;
  fileBaseName: string;
}) {
  if (!dataUrl) {
    return (
      <div className="flex gap-2">
        <span className="w-32 shrink-0 text-gray-500">{label}</span>
        <span className="text-gray-400">없음</span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-2">
        <span className="w-32 shrink-0 text-gray-500">{label}</span>
        <a
          className="text-blue-600 underline"
          href={dataUrl}
          download={`${fileBaseName}.${fileExtOf(dataUrl)}`}
        >
          다운로드
        </a>
      </div>
      {dataUrl.startsWith("data:image") && (
        <img
          className="max-h-48 w-fit rounded-lg border border-gray-200 object-contain"
          src={dataUrl}
          alt={label}
        />
      )}
    </div>
  );
}

// ---------- 코드별 성과 ----------

function PerformanceSection({ codes }: { codes: ReferralAdminCodeDetail[] }) {
  const [selectedCode, setSelectedCode] = useState("");
  const [tab, setTab] = useState<"daily" | "monthly">("monthly");
  const [result, setResult] = useState<AmbassadorDashboardResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = async (code: string) => {
    setSelectedCode(code);
    setResult(null);
    setMessage(null);
    if (!code) return;
    setLoading(true);
    try {
      const res = await adminApi.performance(code);
      if (res.code === 0 && res.result) setResult(res.result);
      else setMessage(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  const buckets = (tab === "daily" ? result?.daily : result?.monthly) ?? [];

  return (
    <section>
      <h2 className="text-base font-bold text-gray-900">크리에이터별 성과</h2>
      <div className="mt-3 flex items-center gap-2">
        <select
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none"
          value={selectedCode}
          onChange={(e) => load(e.target.value)}
        >
          <option value="">크리에이터 선택</option>
          {codes.map((detail) => (
            <option key={detail.code} value={detail.code}>
              {detail.name ?? detail.code}
            </option>
          ))}
        </select>
        {result && (
          <div className="flex gap-1">
            {(["monthly", "daily"] as const).map((t) => (
              <button
                key={t}
                className={`rounded-full px-3 py-1 text-sm ${
                  tab === t
                    ? "bg-gray-900 text-white"
                    : "border border-gray-200 bg-white text-gray-500"
                }`}
                onClick={() => setTab(t)}
              >
                {t === "monthly" ? "월별" : "일별"}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && <p className="mt-3 text-sm text-gray-500">불러오는 중...</p>}
      {message && <p className="mt-3 text-sm text-red-500">{message}</p>}

      {result && (
        <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-100 text-left text-gray-500">
                <th className="px-3 py-3 font-medium">
                  {tab === "daily" ? "날짜" : "월"}
                </th>
                <th className="px-3 py-3 text-right font-medium">가입자</th>
                <th className="px-3 py-3 text-right font-medium">결제자</th>
                <th className="px-3 py-3 text-right font-medium">환불자</th>
                {tab === "monthly" && (
                  <th className="px-3 py-3 text-right font-medium">정산액</th>
                )}
              </tr>
            </thead>
            <tbody>
              {buckets.length === 0 && (
                <tr>
                  <td
                    className="px-3 py-6 text-center text-gray-400"
                    colSpan={tab === "monthly" ? 5 : 4}
                  >
                    아직 집계된 데이터가 없어요
                  </td>
                </tr>
              )}
              {buckets.map((bucket) => (
                <tr
                  key={bucket.period}
                  className="border-b border-gray-100 last:border-b-0"
                >
                  <td className="px-3 py-3 text-gray-900">{bucket.period}</td>
                  <td className="px-3 py-3 text-right text-gray-900">
                    {bucket.signupCount}
                  </td>
                  <td className="px-3 py-3 text-right text-gray-900">
                    {bucket.payerCount}
                  </td>
                  <td className="px-3 py-3 text-right text-gray-900">
                    {bucket.refundCount}
                  </td>
                  {tab === "monthly" && (
                    <td className="px-3 py-3 text-right text-gray-900">
                      {bucket.settlementAmount != null
                        ? `${bucket.settlementAmount.toLocaleString()}원`
                        : "-"}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------- 월별 정산 ----------

function SettlementSection() {
  const [month, setMonth] = useState(currentMonth());
  const [result, setResult] = useState<ReferralAdminSettlementResult | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = async (targetMonth: string) => {
    setMonth(targetMonth);
    setResult(null);
    setMessage(null);
    if (!targetMonth) return;
    setLoading(true);
    try {
      const res = await adminApi.settlement(targetMonth);
      if (res.code === 0 && res.result) setResult(res.result);
      else setMessage(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(currentMonth());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section>
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold text-gray-900">월별 정산</h2>
        <input
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none"
          type="month"
          value={month}
          onChange={(e) => load(e.target.value)}
        />
      </div>

      {loading && <p className="mt-3 text-sm text-gray-500">불러오는 중...</p>}
      {message && <p className="mt-3 text-sm text-red-500">{message}</p>}

      {result && (
        <>
          <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-100 text-left text-gray-500">
                  <th className="px-3 py-3 font-medium">이름</th>
                  <th className="px-3 py-3 font-medium">코드</th>
                  <th className="px-3 py-3 font-medium">전화번호</th>
                  <th className="px-3 py-3 text-right font-medium">결제자</th>
                  <th className="px-3 py-3 text-right font-medium">환불자</th>
                  <th className="px-3 py-3 text-right font-medium">단가</th>
                  <th className="px-3 py-3 text-right font-medium">정산액</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.length === 0 && (
                  <tr>
                    <td
                      className="px-3 py-6 text-center text-gray-400"
                      colSpan={7}
                    >
                      앰버서더 연결된 코드가 없어요
                    </td>
                  </tr>
                )}
                {result.rows.map((row) => (
                  <tr
                    key={row.code}
                    className="border-b border-gray-100 last:border-b-0"
                  >
                    <td className="px-3 py-3 text-gray-900">
                      {row.name ?? "-"}
                    </td>
                    <td className="px-3 py-3 font-medium text-gray-900">
                      {row.code}
                    </td>
                    <td className="px-3 py-3 text-gray-900">
                      {row.ambassadorPhoneNumber
                        ? formatPhoneNumberInput(row.ambassadorPhoneNumber)
                        : "-"}
                    </td>
                    <td className="px-3 py-3 text-right text-gray-900">
                      {row.payerCount}
                    </td>
                    <td className="px-3 py-3 text-right text-gray-900">
                      {row.refundCount}
                    </td>
                    <td className="px-3 py-3 text-right text-gray-900">
                      {row.perPayerAmount != null
                        ? `${row.perPayerAmount.toLocaleString()}원`
                        : "-"}
                    </td>
                    <td className="px-3 py-3 text-right font-medium text-gray-900">
                      {row.settlementAmount != null
                        ? `${row.settlementAmount.toLocaleString()}원`
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-right text-sm font-semibold text-gray-900">
            합계 {result.totalAmount.toLocaleString()}원
          </p>
          <p className="mt-1 text-right text-xs text-gray-400">
            정산액 = (해당 월 기여 확정 결제자 − 환불자) × 해당 월 적용 단가.
            이전 달 결제 건의 환불은 해당 월에 음수(차감)로 잡히고, 이후에
            단가를 바꿔도 지난 달 표는 바뀌지 않아요.
          </p>
        </>
      )}
    </section>
  );
}
